import axios from 'axios';

// Joi messages from the backend validate middleware look like: "field" must be ..., ""requestId"" must be a valid mongo id
const JOI_PART = /^"+[\w.[\]-]+"+ (must|is|length|contains|does|fails|failed|with|not|cannot|has|at)\b/;

const apiPath = (url) => String(url || '').split('?')[0].replace(/^https?:\/\/[^/]+/i, '');

/** True when a request path has an id segment that was never set: "undefined", "null" or an empty segment. */
export const hasMissingId = (url) => {
    const segs = apiPath(url).split('/');
    return segs.some((seg, i) => seg === 'undefined' || seg === 'null' || (seg === '' && i > 0 && i < segs.length - 1));
};

export const isObjectId = (v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

export const isValidationMessage = (m) =>
    typeof m === 'string' && m.split(', ').some((part) => JOI_PART.test(part.trim()));

export const friendlyValidationMessage = (m) => {
    if (/must be a valid mongo id/.test(m)) return 'This record could not be found. Please go back and open it again.';
    if (/is required|is not allowed to be empty/.test(m)) return 'Please fill in all required fields and try again.';
    if (/"email"+ must be a valid email/.test(m)) return 'Please enter a valid email address.';
    return 'Some of the information entered is not valid. Please check it and try again.';
};

/** User-facing text for an axios error; raw validation details stay in the console only. */
export const friendlyErrorMessage = (err, fallback = 'Request failed') => {
    if (err?.vpMissingId) return 'This record could not be found. Please go back and open it again.';
    const m = err?.response?.data?.message;
    if (m) return isValidationMessage(m) ? friendlyValidationMessage(m) : m;
    return err?.message || fallback;
};

let installed = false;
/** Rewrites 400 Joi validation messages to friendly text for every screen that shows response.data.message. */
export const installFriendlyErrors = (client = axios) => {
    if (installed) return;
    installed = true;
    // never call an :id route with an id that was never set (".../undefined", ".../null", "...//")
    client.interceptors.request.use((cfg) => {
        if (hasMissingId(cfg?.url)) {
            // eslint-disable-next-line no-console
            console.warn(`[api] blocked ${String(cfg.method || '').toUpperCase()} ${apiPath(cfg.url)}: missing id`);
            return Promise.reject(Object.assign(new Error('Missing id: request not sent'), { config: cfg, vpMissingId: true }));
        }
        return cfg;
    });
    client.interceptors.response.use(undefined, (error) => {
        const data = error?.response?.data;
        if (error?.response?.status === 400 && data && isValidationMessage(data.message)) {
            const path = apiPath(error.config?.url);
            // eslint-disable-next-line no-console
            console.warn(`[api] ${String(error.config?.method || '').toUpperCase()} ${path} validation: ${data.message}`);
            data.validationMessage = data.message;
            data.message = friendlyValidationMessage(data.message);
        }
        return Promise.reject(error);
    });
};
